import type { MaybeRefOrGetter, WatchStopHandle } from 'vue';
import type {
	BlockRootContext,
	BlockRootContextPropertyBlock,
	BlockRootContextPropertyResult,
} from '#layers/block-engine/types/block';
import { tryOnScopeDispose } from '@vueuse/core';
import isEqual from 'fast-deep-equal';
import { computed, shallowReactive, toValue, watch } from 'vue';

export interface BlockRootContextApi {
	/**
	 * Returns the merged root context visible to the current block tree.
	 */
	getRootContext: () => BlockRootContext;
	/**
	 * Returns all published values for one top-level root context key.
	 */
	getRootContextProperty: <TValue = unknown>(key: string) => Array<BlockRootContextPropertyResult<TValue>>;
}

interface BlockRootContextPropertySource {
	id: symbol;
	blockKey: object | symbol;
	getBlock: () => BlockRootContextPropertyBlock;
}

interface PublishBlockRootContextPropertyOptions {
	blockKey: object | symbol;
	getBlock: () => BlockRootContextPropertyBlock;
}

export interface BlockRootContextController extends BlockRootContextApi {
	/**
	 * Publishes one reactive root context property value for a rendered block.
	 */
	publishRootContextProperty: <TValue = unknown>(
		key: string,
		value: MaybeRefOrGetter<TValue | undefined>,
		options: PublishBlockRootContextPropertyOptions,
	) => WatchStopHandle;
}

interface BlockRootContextPropertyEntry {
	key: string;
	value: unknown;
	source: BlockRootContextPropertySource;
}

interface BlockRootContextPropertyPublisher {
	cleanup: WatchStopHandle;
}

interface CreateBlockRootContextApiOptions {
	getBaseRootContext: () => BlockRootContext | undefined;
	getRootBlock: () => BlockRootContextPropertyBlock['rootBlock'];
	parentApi?: BlockRootContextController;
}

/**
 * Creates the root context controller for one block render boundary.
 */
export function createBlockRootContextApi(options: CreateBlockRootContextApiOptions): BlockRootContextController {
	const entries = shallowReactive(new Map<symbol, BlockRootContextPropertyEntry>());
	const publishers = new Map<symbol, BlockRootContextPropertyPublisher>();
	const activePublishers = new Map<string, Map<object | symbol, symbol>>();

	const rootContext = computed<BlockRootContext>(() => ({
		...options.parentApi?.getRootContext(),
		...options.getBaseRootContext(),
	}));

	function publishRootContextProperty<TValue = unknown>(
		key: string,
		value: MaybeRefOrGetter<TValue | undefined>,
		publishOptions: PublishBlockRootContextPropertyOptions,
	): WatchStopHandle {
		if (options.parentApi) {
			return options.parentApi.publishRootContextProperty(key, value, publishOptions);
		}

		stopActivePublisher(key, publishOptions.blockKey);

		const source: BlockRootContextPropertySource = {
			id: Symbol(key),
			blockKey: publishOptions.blockKey,
			getBlock: publishOptions.getBlock,
		};

		let isCleanedUp = false;
		let stop: WatchStopHandle | undefined;

		const cleanup = () => {
			if (isCleanedUp) return;

			isCleanedUp = true;
			stop?.();
			entries.delete(source.id);
			publishers.delete(source.id);

			const activePublisherId = activePublishers.get(key)?.get(source.blockKey);

			if (activePublisherId === source.id) {
				const publishersByBlock = activePublishers.get(key);

				publishersByBlock?.delete(source.blockKey);

				if (publishersByBlock?.size === 0) {
					activePublishers.delete(key);
				}
			}
		};

		publishers.set(source.id, {
			cleanup,
		});

		const publishersByBlock = activePublishers.get(key) ?? new Map<object | symbol, symbol>();
		activePublishers.set(key, publishersByBlock);
		publishersByBlock.set(source.blockKey, source.id);

		stop = watch(
			() => toValue(value),
			(nextValue) => {
				if (isCleanedUp) return;

				if (nextValue === undefined) {
					entries.delete(source.id);
					return;
				}

				// Avoid feedback loops when a publisher derives from this same property list.
				const existingEntry = entries.get(source.id);

				if (existingEntry && existingEntry.key === key && rootContextValuesAreEqual(existingEntry.value, nextValue)) {
					return;
				}

				entries.set(source.id, { key, value: nextValue, source });
			},
			{
				deep: true,
				immediate: true,
			},
		);

		tryOnScopeDispose(cleanup);

		return cleanup;
	}

	function getRootContextProperty<TValue = unknown>(key: string): Array<BlockRootContextPropertyResult<TValue>> {
		const results = [...(options.parentApi?.getRootContextProperty<TValue>(key) ?? [])];
		const baseRootContext = options.getBaseRootContext();
		const baseValue = (baseRootContext as Record<string, TValue | undefined> | undefined)?.[key];

		if (baseValue !== undefined) {
			results.push({
				block: getSyntheticRootContextPropertyBlock(),
				value: baseValue as Exclude<TValue, undefined>,
			});
		}

		for (const entry of entries.values()) {
			if (entry.key === key) {
				results.push({
					block: entry.source.getBlock(),
					value: entry.value as Exclude<TValue, undefined>,
				});
			}
		}

		return results;
	}

	return {
		getRootContext: () => rootContext.value,
		getRootContextProperty,
		publishRootContextProperty,
	};

	function stopActivePublisher(key: string, blockKey: object | symbol) {
		const publisherId = activePublishers.get(key)?.get(blockKey);
		if (publisherId) publishers.get(publisherId)?.cleanup();
	}

	function getSyntheticRootContextPropertyBlock(): BlockRootContextPropertyBlock {
		return {
			child: null,
			definition: null,
			rootBlock: options.getRootBlock(),
		};
	}
}

function rootContextValuesAreEqual(a: unknown, b: unknown): boolean {
	if (Object.is(a, b)) return true;

	try {
		return isEqual(a, b);
	}
	catch {
		return false;
	}
}
